import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('day'); // 'day', 'night', 'neurodivergent', 'sigma'
  const [autoMode, setAutoMode] = useState(false);

  useEffect(() => {
    // Load saved preferences
    const savedTheme = localStorage.getItem('theme');
    const savedAutoMode = localStorage.getItem('autoMode') === 'true';
    
    if (savedTheme) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    }
    
    setAutoMode(savedAutoMode);
    
    if (savedAutoMode) {
      checkTimeAndSetTheme();
    }
  }, []);

  useEffect(() => {
    if (autoMode) {
      const interval = setInterval(checkTimeAndSetTheme, 60000); // Check every minute
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMode]);

  const checkTimeAndSetTheme = () => {
    const hour = new Date().getHours();
    const newTheme = (hour >= 6 && hour < 18) ? 'day' : 'night';
    
    if (newTheme !== theme && (theme === 'day' || theme === 'night')) {
      setTheme(newTheme);
      applyTheme(newTheme);
    }
  };

  const applyTheme = (newTheme) => {
    // Remove all theme classes
    document.body.classList.remove('night-mode', 'neurodivergent-mode', 'sigma-mode');
    
    // Apply new theme class
    if (newTheme === 'night') {
      document.body.classList.add('night-mode');
    } else if (newTheme === 'neurodivergent') {
      document.body.classList.add('neurodivergent-mode');
    } else if (newTheme === 'sigma') {
      document.body.classList.add('sigma-mode');
    }
    
    localStorage.setItem('theme', newTheme);
  };

  const setManualTheme = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    setAutoMode(false);
    localStorage.setItem('autoMode', 'false');
  };

  const toggleAutoMode = () => {
    const newAutoMode = !autoMode;
    setAutoMode(newAutoMode);
    localStorage.setItem('autoMode', String(newAutoMode));
    
    if (newAutoMode) {
      checkTimeAndSetTheme();
    }
  };

  const value = {
    theme,
    autoMode,
    setTheme: setManualTheme,
    toggleAutoMode,
    isDark: theme === 'night' || theme === 'sigma'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
